import { Chip, Dialog, Autocomplete, TextField } from "@mui/material";
import { isEqual } from "lodash";
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { AnomalyTypeRef } from "src/models/tna/tna-anomalies/anomaly-type/anomaly-type-ref";
import AddEditButton from "src/reusable-components/AddEditButton/AddEditButton";
import { State, Props } from "./interfaces";
import { DialogContent } from "./styles";
import ConfirmationChangesDialog from "../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../DialogActions/DialogActions";
import DialogTitle from "../../../DialogTitle/DialogTitle";

const AddEditIncludedAnomalyTypes = ({
  item,
  handleNotifyParent,
  onLocalSave,
  children,
  viewMode = false,
  buttonText,
  effect,
  disabled,
  customButtonStyle,
  selectedAnomalyTypes = [],
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.edit_anomaly_types";

  const [state, setState] = useState<State>({
    openDialog: false,
    loading: false,
    item,
    selectedAnomalyTypes,
    error: "",
    CCDOpen: false,
  });

  const hasChanges = useCallback((): boolean => {
    const currentIds = (state.selectedAnomalyTypes || []).map((t) => t.id).sort();
    const originalIds = (selectedAnomalyTypes || []).map((t) => t.id).sort();
    return !isEqual(currentIds, originalIds);
  }, [state.selectedAnomalyTypes, selectedAnomalyTypes]);

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      if (onLocalSave) {
        onLocalSave(state.item, state.selectedAnomalyTypes);
        setState((state) => ({ ...state, openDialog: false, loading: false }));
        return;
      }

      // TODO: Replace with actual API call to save comment

      // const response = { error: "" };

      // if (!response.error) {
      //   setState((state) => ({ ...state, openDialog: false, loading: false }));
      //   // if (notifyParent) {
      //   //   notifyParent();
      //   // }
      // } else {
      //   setState((state) => ({
      //     ...state,
      //     error: response.error,
      //     loading: false,
      //   }));
      // }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string, loading: false }));
    }
  }, [onLocalSave, state.item, state.selectedAnomalyTypes]);

  const areFieldsValidated = useCallback((): boolean => {
    const hasSelections = state.selectedAnomalyTypes && state.selectedAnomalyTypes.length > 0;
    return hasSelections && hasChanges();
  }, [state.selectedAnomalyTypes, hasChanges]);

  return (
    <>
      <AddEditButton
        title={
          state.selectedAnomalyTypes && state.selectedAnomalyTypes.length > 0
            ? t(`${tRP}.edit_title`)
            : t(`${tRP}.add_title`)
        }
        effect={effect}
        disabled={disabled}
        onClick={() => {
          setState((state) => ({ ...state, openDialog: true }));
        }}
        buttonText={buttonText}
        customStyle={customButtonStyle}
      >
        {children}
      </AddEditButton>
      <Dialog
        open={state.openDialog}
        onClose={() => {
          if (hasChanges()) {
            setState((state) => ({ ...state, CCDOpen: true }));
          } else {
            setState((state) => ({ ...state, openDialog: false }));
          }
        }}
        fullWidth
        sx={{
          ".MuiDialogContent-root": {
            height: "120px !important",
            minHeight: "120px !important",
          },
        }}
      >
        <DialogTitle
          title={t(`${tRP}.edit_title`)}
          handleClose={() => {
            if (hasChanges()) {
              setState((state) => ({ ...state, CCDOpen: true }));
            } else {
              setState((state) => ({ ...state, openDialog: false }));
            }
          }}
        />
        <DialogContent>
          <Autocomplete
            multiple
            filterOptions={(options) => options}
            options={(state.item.anomalies || []).map((anomaly) => ({
              id: anomaly.anomalyType?.id || "",
              anomalyType: anomaly.anomalyType,
            }))}
            getOptionLabel={(option) => option.anomalyType?.description || ""}
            value={
              (state.selectedAnomalyTypes || []).length > 0
                ? (state.item.anomalies || [])
                    .map((anomaly) => ({
                      id: anomaly.anomalyType?.id || "",
                      anomalyType: anomaly.anomalyType,
                    }))
                    .filter((option) => (state.selectedAnomalyTypes || []).some((meta) => meta.id === option.id))
                : []
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, newValue) => {
              const selectedAnomalyTypes = newValue
                .map((v) => v.anomalyType)
                .filter((at): at is AnomalyTypeRef => at !== null);
              setState((prevState) => ({
                ...prevState,
                selectedAnomalyTypes,
              }));
            }}
            disabled={viewMode}
            ListboxProps={{
              role: "list-box",
              style: { maxHeight: "200px", background: "white" },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={t(`${tRP}.select_anomalies`)}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.anomalyType?.description || ""}
                    {...tagProps}
                    size="small"
                  />
                );
              })
            }
            sx={{ width: "100%" }}
          />
        </DialogContent>
        <DialogActions
          handleCancel={() => {
            if (hasChanges()) {
              setState((state) => ({ ...state, CCDOpen: true }));
            } else {
              setState((state) => ({ ...state, openDialog: false }));
            }
          }}
          handleSave={handleSave}
          loading={state.loading}
          disabledSaveButton={viewMode || !areFieldsValidated()}
          error={state.error}
        />
      </Dialog>

      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            ...state,
            openDialog: false,
            item,
            selectedAnomalyTypes,
            CCDOpen: false,
            error: "",
          }))
        }
      />
    </>
  );
};

export default React.memo(AddEditIncludedAnomalyTypes);
